import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '@/hen-ui/utils';
import { flexVariants, type FlexVariantProps } from './flex.variants';

export interface FlexProps
  extends React.HTMLAttributes<HTMLDivElement>,
    FlexVariantProps {
  /** Render as child element using Radix Slot */
  asChild?: boolean;
}

/**
 * Flex - Flexible layout component for vertical and horizontal arrangements
 *
 * Consolidates Stack (column) and Cluster (row) patterns into a single component.
 * Use `direction="column"` for vertical stacking, `direction="row"` for horizontal layouts.
 *
 * @example Vertical stack (replaces Stack)
 * ```tsx
 * <Flex direction="column" gap="lg">
 *   <Heading variant="h3">Title</Heading>
 *   <Text variant="b3">Description</Text>
 *   <Button>Action</Button>
 * </Flex>
 * ```
 *
 * @example Horizontal layout (replaces Cluster)
 * ```tsx
 * <Flex direction="row" gap="md">
 *   <Button variant="primaryFill">Primary</Button>
 *   <Button variant="textOnly">Secondary</Button>
 * </Flex>
 * ```
 *
 * @example Wrapping tags
 * ```tsx
 * <Flex direction="row" gap="sm" wrap="wrap">
 *   <Badge>Tag 1</Badge>
 *   <Badge>Tag 2</Badge>
 *   <Badge>Tag 3</Badge>
 * </Flex>
 * ```
 *
 * @example Centered content
 * ```tsx
 * <Flex direction="row" justify="center" gap="lg">
 *   <Icon name="star" />
 *   <Text>Centered content</Text>
 * </Flex>
 * ```
 *
 * @example Responsive direction (column on mobile, row from md up, like Grid)
 * ```tsx
 * <Flex direction="column" direction-md="row" gap="md">
 *   <span>Item 1</span>
 *   <span>Item 2</span>
 * </Flex>
 * ```
 */
export const Flex = React.forwardRef<HTMLDivElement, FlexProps>(
  (
    {
      className,
      direction = 'column',
      'direction-md': directionMd,
      'direction-lg': directionLg,
      gap = 'md',
      wrap = 'nowrap',
      align = 'stretch',
      justify = 'start',
      asChild = false,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'div';

    return (
      <Comp
        className={cn(
          flexVariants({
            direction,
            'direction-md': directionMd,
            'direction-lg': directionLg,
            gap,
            wrap,
            align,
            justify,
          }),
          className,
        )}
        ref={ref}
        {...props}
      >
        {children}
      </Comp>
    );
  },
);

Flex.displayName = 'Flex';
